/**
 * Tab Session Management Utilities
 *
 * Uses sessionStorage to track session data per browser tab.
 * This prevents conflicts when multiple tabs have different sessions
 * but share the same localStorage.
 */

import type { Environment } from "@/repositories/process/types";

const TAB_SESSION_KEY = "tab-session-data";
const TAB_ENVIRONMENT_KEY = "tab-environment-data";
const PENDING_PROCESS_URL_KEY = "pending-process-url";
const SIGNOUT_REASON_KEY = "signout-reason";
const SIGNOUT_ORIGIN_KEY = "signout-origin";
const SIGNOUT_CREATED_AT_KEY = "signout-created-at";
const CONTROLLED_REALM_SWITCH_ORIGIN = "controlled-realm-switch";
const CONTROLLED_REALM_SWITCH_REASON = "realm-switch-to-controlled";

export interface SignoutTransitionData {
  pendingProcessUrl: string | undefined;
  signoutReason: string | undefined;
  signoutOrigin: string | undefined;
  signoutCreatedAt: string | undefined;
}

export interface TabSessionData {
  /** The identification (DNI/document) of the logged-in user */
  identification: string;
  /** The environment/process ID the user logged into */
  environmentId: string;
  /** The environment URL (slug) for the process */
  environmentUrl: string;
  /** The realm used for authentication */
  realm: "evbasic" | "evcore";
  /** Timestamp when the session was created */
  createdAt: number;
}

export interface TabEnvironmentData {
  /** The full environment object for this tab */
  environment: Environment;
  /** The environment URL (slug) for the process */
  environmentUrl: string;
  /** Timestamp when the data was saved */
  savedAt: number;
}

/**
 * Save session data for the current tab
 */
export function saveTabSession(data: Omit<TabSessionData, "createdAt">): void {
  if (typeof window === "undefined") {
    return;
  }

  const sessionData: TabSessionData = {
    ...data,
    createdAt: Date.now(),
  };

  sessionStorage.setItem(TAB_SESSION_KEY, JSON.stringify(sessionData));
}

/**
 * Get session data for the current tab
 */
export function getTabSession(): TabSessionData | null {
  if (typeof window === "undefined") {
    return null;
  }

  const data = sessionStorage.getItem(TAB_SESSION_KEY);
  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data) as TabSessionData;
  } catch {
    return null;
  }
}

/**
 * Clear session data for the current tab
 */
export function clearTabSession(): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(TAB_SESSION_KEY);
}

/**
 * Check if the current tab has an active session
 */
export function hasTabSession(): boolean {
  return getTabSession() !== null;
}

/**
 * Validate that the current token matches the tab session
 * Returns true if valid, false if there's a mismatch
 */
export function validateTabSession(
  currentIdentification: string | undefined,
  currentEnvironmentId: string | undefined
): { isValid: boolean; reason?: string } {
  const tabSession = getTabSession();

  // No tab session means this is a fresh tab or non-controlled flow
  if (!tabSession) {
    return { isValid: true };
  }

  // If we have a tab session but no current identification, session is invalid
  if (!currentIdentification) {
    return {
      isValid: false,
      reason: "No current user identification but tab session exists",
    };
  }

  // Check if identification matches
  if (tabSession.identification !== currentIdentification) {
    return {
      isValid: false,
      reason: `Identification mismatch: tab has "${tabSession.identification}" but token has "${currentIdentification}"`,
    };
  }

  // Check if environment matches (if provided)
  if (currentEnvironmentId && tabSession.environmentId !== currentEnvironmentId) {
    return {
      isValid: false,
      reason: `Environment mismatch: tab has "${tabSession.environmentId}" but current is "${currentEnvironmentId}"`,
    };
  }

  return { isValid: true };
}

/**
 * Save environment data for the current tab
 * This allows each tab to maintain its own environment independently
 */
export function saveTabEnvironment(
  environment: Environment,
  environmentUrl: string
): void {
  if (typeof window === "undefined") {
    return;
  }

  const data: TabEnvironmentData = {
    environment,
    environmentUrl,
    savedAt: Date.now(),
  };

  sessionStorage.setItem(TAB_ENVIRONMENT_KEY, JSON.stringify(data));
}

/**
 * Get environment data for the current tab
 */
export function getTabEnvironment(): TabEnvironmentData | null {
  if (typeof window === "undefined") {
    return null;
  }

  const data = sessionStorage.getItem(TAB_ENVIRONMENT_KEY);
  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data) as TabEnvironmentData;
  } catch {
    return null;
  }
}

/**
 * Clear environment data for the current tab
 */
export function clearTabEnvironment(): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(TAB_ENVIRONMENT_KEY);
}

export function setControlledRealmSwitchSignout(processUrl: string): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.setItem(PENDING_PROCESS_URL_KEY, processUrl);
  sessionStorage.setItem(SIGNOUT_REASON_KEY, CONTROLLED_REALM_SWITCH_REASON);
  sessionStorage.setItem(SIGNOUT_ORIGIN_KEY, CONTROLLED_REALM_SWITCH_ORIGIN);
  sessionStorage.setItem(SIGNOUT_CREATED_AT_KEY, String(Date.now()));
}

export function consumeSignoutTransitionState(): SignoutTransitionData {
  if (typeof window === "undefined") {
    return {
      pendingProcessUrl: undefined,
      signoutReason: undefined,
      signoutOrigin: undefined,
      signoutCreatedAt: undefined,
    };
  }

  const pendingProcessUrl = sessionStorage.getItem(PENDING_PROCESS_URL_KEY) || undefined;
  const signoutReason = sessionStorage.getItem(SIGNOUT_REASON_KEY) || undefined;
  const signoutOrigin = sessionStorage.getItem(SIGNOUT_ORIGIN_KEY) || undefined;
  const signoutCreatedAt = sessionStorage.getItem(SIGNOUT_CREATED_AT_KEY) || undefined;

  sessionStorage.removeItem(PENDING_PROCESS_URL_KEY);
  sessionStorage.removeItem(SIGNOUT_REASON_KEY);
  sessionStorage.removeItem(SIGNOUT_ORIGIN_KEY);
  sessionStorage.removeItem(SIGNOUT_CREATED_AT_KEY);

  return {
    pendingProcessUrl,
    signoutReason,
    signoutOrigin,
    signoutCreatedAt,
  };
}

export function clearSignoutTransitionState(): void {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(PENDING_PROCESS_URL_KEY);
  sessionStorage.removeItem(SIGNOUT_REASON_KEY);
  sessionStorage.removeItem(SIGNOUT_ORIGIN_KEY);
  sessionStorage.removeItem(SIGNOUT_CREATED_AT_KEY);
}
